/* Whisper: available products first, across the entire filtered collection.
 * Progressive enhancement: keep native Liquid output if requests fail.
 */
(() => {
  const PAGE_SIZE = 24;
  const CARD_SELECTOR = '[ref="grid"] > [data-product-id]';
  const instances = new WeakSet();

  function init(root) {
    if (instances.has(root)) return;
    instances.add(root);
    const sectionId = root.getAttribute('section-id');
    if (!sectionId) return;
    let revision = 0;
    let lastContext = '';
    let lastPage = 0;
    let active = false;
    let busy = false;
    const cache = new Map();
    const nav = document.createElement('nav');
    nav.className = 'availability-first-pagination';
    nav.setAttribute('aria-label', 'Paginación de productos');

    function urlForAvailability(available, page) {
      const u = new URL(location.href);
      u.searchParams.delete('section_id');
      u.searchParams.delete('sections');
      u.searchParams.set('filter.v.availability', available ? '1' : '0');
      u.searchParams.set('page', String(page));
      u.searchParams.set('section_id', sectionId);
      u.hash = '';
      return u;
    }

    async function getPage(available, page) {
      const url = urlForAvailability(available, page);
      const key = url.toString();
      if (cache.has(key)) return cache.get(key);
      const promise = (async () => {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('[ref="grid"]');
        if (!grid) throw new Error('Whisper no devolvió [ref="grid"]');
        const cards = Array.from(grid.querySelectorAll(':scope > [data-product-id]'));
        const last = Number(grid.dataset.lastPage || 1);
        if (!Number.isSafeInteger(last) || last < 1) throw new Error('Número de páginas no válido');
        return { cards, last };
      })();
      cache.set(key, promise);
      promise.catch(() => cache.delete(key));
      return promise;
    }

    async function groupCount(available) {
      const first = await getPage(available, 1);
      if (first.cards.length === 0) return 0;
      if (first.last === 1) return first.cards.length;
      const tail = await getPage(available, first.last);
      return (first.last - 1) * PAGE_SIZE + tail.cards.length;
    }

    async function sliceGroup(available, start, count) {
      if (count <= 0) return [];
      const startPage = Math.floor(start / PAGE_SIZE) + 1;
      const startOffset = start % PAGE_SIZE;
      const a = await getPage(available, startPage);
      let cards = a.cards.slice(startOffset, startOffset + count);
      if (cards.length < count && startPage < a.last) {
        const b = await getPage(available, startPage + 1);
        cards = cards.concat(b.cards.slice(0, count - cards.length));
      }
      return cards;
    }

    function contextKey() {
      const u = new URL(location.href);
      u.searchParams.delete('page');
      u.searchParams.delete('section_id');
      u.searchParams.delete('sections');
      u.hash = '';
      return u.toString();
    }

    function pageNumber() {
      const n = Number(new URL(location.href).searchParams.get('page') || 1);
      return Number.isSafeInteger(n) && n > 0 ? n : 1;
    }

    function pageUrl(page) {
      const u = new URL(location.href);
      u.searchParams.set('page', String(page));
      return u;
    }

    function hideNativePager() {
      // Only hide pagination containers containing links to collection pages;
      // never hide filters or the product grid itself.
      const grid = root.querySelector('[ref="grid"]');
      if (!grid) return;
      const candidates = root.querySelectorAll('nav, .pagination, .pagination-wrapper, pagination-component');
      for (const el of candidates) {
        if (el === nav || el.contains(nav) || el.contains(grid) || el.closest('.facets-block-wrapper')) continue;
        if (el.querySelector('a[href*="page="]')) el.hidden = true;
      }
    }

    function buildNav(current, totalPages) {
      nav.replaceChildren();
      if (totalPages < 2) return;
      const makeLink = (label, page, selected = false) => {
        const a = document.createElement('a');
        a.href = pageUrl(page).toString();
        a.textContent = label;
        if (selected) a.setAttribute('aria-current', 'page');
        a.dataset.availabilityPage = String(page);
        nav.append(a);
      };
      if (current > 1) makeLink('‹ Anterior', current - 1);
      const start = Math.max(1, current - 2);
      const end = Math.min(totalPages, current + 2);
      if (start > 1) { makeLink('1', 1); if (start > 2) nav.append(' … '); }
      for (let p = start; p <= end; p++) makeLink(String(p), p, p === current);
      if (end < totalPages) { if (end < totalPages - 1) nav.append(' … '); makeLink(String(totalPages), totalPages); }
      if (current < totalPages) makeLink('Siguiente ›', current + 1);
    }

    async function render(force = false) {
      if (!root.isConnected) return;
      const key = contextKey();
      const desired = pageNumber();
      if (!force && active && key === lastContext && desired === lastPage) return;
      const run = ++revision;
      busy = true;
      nav.setAttribute('aria-busy', 'true');
      try {
        const u = new URL(location.href);
        const selectedAvailability = u.searchParams.get('filter.v.availability');
        // Respect an explicit availability filter instead of overriding it.
        const includeAvailable = selectedAvailability !== '0';
        const includeSold = selectedAvailability !== '1';
        const [availableCount, soldCount] = await Promise.all([
          includeAvailable ? groupCount(true) : Promise.resolve(0),
          includeSold ? groupCount(false) : Promise.resolve(0)
        ]);
        const total = availableCount + soldCount;
        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        const current = Math.min(desired, totalPages);
        const offset = (current - 1) * PAGE_SIZE;
        const availableStart = Math.min(offset, availableCount);
        const availableTake = Math.max(0, Math.min(PAGE_SIZE, availableCount - availableStart));
        const soldStart = Math.max(0, offset - availableCount);
        const soldTake = Math.min(PAGE_SIZE - availableTake, Math.max(0, soldCount - soldStart));
        const [availableCards, soldCards] = await Promise.all([
          includeAvailable ? sliceGroup(true, availableStart, availableTake) : Promise.resolve([]),
          includeSold ? sliceGroup(false, soldStart, soldTake) : Promise.resolve([])
        ]);
        if (run !== revision) return;
        const grid = root.querySelector('[ref="grid"]');
        if (!grid) throw new Error('No existe la cuadrícula de productos');
        const cards = [...availableCards, ...soldCards];
        // DOM nodes parsed with DOMParser will be adopted automatically.
        cards.forEach((card, i) => {
          card.dataset.page = String(current);
          for (const name of Array.from(card.classList)) {
            if (/^product-grid__item--\d+$/.test(name)) card.classList.remove(name);
          }
          card.classList.add(`product-grid__item--${i}`);
        });
        grid.replaceChildren(...cards);
        grid.dataset.lastPage = String(totalPages);
        hideNativePager();
        buildNav(current, totalPages);
        if (!nav.isConnected) grid.after(nav);
        nav.hidden = false;
        lastContext = key;
        lastPage = current;
        active = true;
      } catch (error) {
        console.error('[availability-first]', error);
        // On error, reload native Shopify rendering rather than show mixed data.
        nav.hidden = true;
        if (active) window.location.reload();
      } finally {
        if (run === revision) { busy = false; nav.removeAttribute('aria-busy'); }
      }
    }

    nav.addEventListener('click', (event) => {
      const a = event.target.closest('a[data-availability-page]');
      if (!a || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const nextPage = Number(a.dataset.availabilityPage);
      history.pushState({}, '', pageUrl(nextPage));
      render();
      root.scrollIntoView({ block: 'start' });
    });
    window.addEventListener('popstate', () => render());

    // Whisper updates collection HTML asynchronously when sorting/filtering.
    // Detect both URL changes and replaced grids without editing theme internals.
    let seenHref = location.href;
    let seenGrid = root.querySelector('[ref="grid"]');
    const watcher = setInterval(() => {
      if (!root.isConnected) { clearInterval(watcher); return; }
      const grid = root.querySelector('[ref="grid"]');
      if (location.href !== seenHref || grid !== seenGrid) {
        seenHref = location.href;
        const gridChanged = grid !== seenGrid;
        seenGrid = grid;
        const nextKey = contextKey();
        if (nextKey !== lastContext) cache.clear();
        render(gridChanged);
      }
    }, 450);

    render();
  }

  function boot() {
    document.querySelectorAll('results-list[section-id]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  // If Whisper replaces the entire results-list after applying a filter, reattach.
  const observer = new MutationObserver(() => boot());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
